import React from 'react';
import styled from 'styled-components';
import { NodeHeader } from './components/NodeHeader';
import { NodeContentWrapper } from './styles';
import { Working } from '@ui/Working';
import { Body5, Body7, Container } from '@ui';
import { NodeDebugger } from './components/NodeDebugger';
import { NodeWrapper } from './components/NodeWrapper';
import QueryTargetHandle from './components/handles/QueryTargetHandle';
import { FlatButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import SelectedDocument from '@containers/DigitalAnalyst/components/SelectedDocument';

const ContentWrapper = styled(NodeContentWrapper)`
  width: 600px;
  min-height: 120px;

  padding-bottom: 40px;

  textarea {
    height: 120px;
    resize: vertical;
  }
`;
const SelectedDocsScrollBox = styled.div`
  max-height: 104px;
  min-height: ${({ $empty }) => ($empty ? '40px' : 'unset')};
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 8px;
`;
const SelectedDocsRowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`;
const ShowMoreButton = styled.span`
  cursor: pointer;
  opacity: 0.7;
  text-transform: uppercase;

  &:hover {
    opacity: 1;
  }
`;

const Content = ({ id, data, value, onTextChange }) => {
  const { isWorking } = data;
  const [showAllDocs, setShowAllDocs] = React.useState(false);

  const onSelectSourcesClick = () => {
    data.actions.openDocSelectionModal(id, data.selectedDocs, (selectedDocs) => {
      data.actions.updateNodeData(id, { selectedDocs });
    });
  };

  const onAdvancedSettingsClick = () => {
    data.actions.openAdvancedSettingsModal(id);
  };

  const onRemoveSelectedDoc = (filename) => {
    data.actions.updateNodeData(id, {
      selectedDocs: data.selectedDocs.filter((d) => d.filename !== filename),
    });
  };

  if (isWorking) {
    return (
      <ContentWrapper>
        <Container>
          <Body7>{value}</Body7>
        </Container>
        <Working />
      </ContentWrapper>
    );
  }
  return (
    <ContentWrapper>
      <textarea placeholder="Enter your question" value={value} onChange={onTextChange} />
      <Container top={24}>
        <Body5 bold>Selected Sources</Body5>
        <Container top={8}>
          <SelectedDocsScrollBox $empty={!data.selectedDocs || data.selectedDocs.length === 0}>
            <SelectedDocsRowWrapper>
              {(showAllDocs ? data.selectedDocs : data.selectedDocs?.slice(0, 10))?.map((doc) => (
                <SelectedDocument key={doc.id} doc={doc} handleRemoveClick={onRemoveSelectedDoc} />
              ))}
            </SelectedDocsRowWrapper>
            {data.selectedDocs?.length > 10 ? (
              <Container top={4}>
                <ShowMoreButton onClick={() => setShowAllDocs((prev) => !prev)}>
                  <Body5>{showAllDocs ? '[SHOW LESS]' : `[+ ${data.selectedDocs.length - 10} MORE]`}</Body5>
                </ShowMoreButton>
              </Container>
            ) : null}
          </SelectedDocsScrollBox>
        </Container>
      </Container>
      <Container top={24} row verticallyCenter className="read-only-hide">
        <Container>
          <FlatButton onClick={onSelectSourcesClick}>Select Sources</FlatButton>
        </Container>
        <Container left={16}>
          <SkeletonButton onClick={onAdvancedSettingsClick}>Advanced Settings</SkeletonButton>
        </Container>
      </Container>
    </ContentWrapper>
  );
};

export const AgentScriptQueryNode = (props) => {
  const { data, id, isConnectable } = props;

  const testPrompt = 'List 10 companies that generate at least $1Billion in revenue from alternative energy';
  const [value, setValue] = React.useState('');

  React.useEffect(() => {
    data.actions.updateNodeData(id, { question: value });
  }, [value]);

  const onTextChange = (e) => {
    setValue(e.target.value);
  };

  const name = 'Question';
  return (
    <NodeWrapper data-id={id} name={name} data={data} nodeData={props}>
      <NodeHeader id={id} name={name} actions={data.actions} />
      <Content id={id} data={data} value={value} onTextChange={onTextChange} />

      <QueryTargetHandle {...props} isConnectable={isConnectable} />
      <NodeDebugger id={id} />
    </NodeWrapper>
  );
};
